import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FlatList,
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { KeyboardSafeForm } from '../../components/KeyboardSafeForm';
import { PermissionGuard } from '../../components/PermissionGuard';
import {
  RequestFormDateField,
  RequestFormTextField,
  RequestFormTimeField,
} from '../../components/RequestFormField';
import { ScreenHeader } from '../../components/ScreenHeader';
import { UnderlineTabs } from '../../components/UnderlineTabs';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { getAttendanceCorrectionModuleCapabilities } from '../../config/attendanceCorrectionCapabilities';
import type { ColorPalette } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { normalizeApiError } from '../../api/apiError';
import { getAttendanceDetails } from '../../services/attendanceService';
import {
  applyAttendanceCorrectionRequest,
  getAttendanceCorrectionRequests,
} from '../../services/attendanceCorrectionService';
import type { AttendanceCorrectionRequestListItem } from '../../types/attendanceCorrection';
import {
  addCorrectionBreak,
  correctionBreaksPayload,
  correctionRemovedBreaksPayload,
  draftFromAttendanceDetails,
  joinedCorrectionBreakTimes,
  removeCorrectionBreak,
  updateCorrectionBreak,
  validateAttendanceCorrectionTimes,
  type AttendanceCorrectionDraft,
} from '../../utils/attendanceCorrectionDraft';
import { attendanceCorrectionErrorMessage } from '../../utils/attendanceCorrectionErrors';
import { isAllowedAttendanceCorrectionDate } from '../../utils/attendanceCorrectionFilters';
import { getAttendanceCorrectionStatusCategory } from '../../utils/attendanceCorrectionStatus';
import {
  canSeeRequestApprovals,
  attendanceCorrectionApprovalListTitle,
} from '../../utils/approvalRoles';
import {
  formatCalendarDate,
  formatPermissionTime,
  parseCalendarDateInput,
  todayBusinessCalendarDate,
} from '../../utils/dateTime';
import { isValidLeaveDate } from '../../utils/leaveFilters';

type Props = NativeStackScreenProps<
  RootStackParamList,
  'AttendanceCorrectionHome'
>;
type CorrectionTab = 'My Requests' | 'Apply Correction';

const CORRECTION_TABS: readonly CorrectionTab[] = [
  'My Requests',
  'Apply Correction',
];

function statusTextColor(statusCode: string | null, palette: ColorPalette) {
  const category = getAttendanceCorrectionStatusCategory(statusCode);
  if (category === 'success') return palette.success;
  if (category === 'danger') return palette.error;
  if (category === 'warning') return palette.warning;
  if (category === 'info') return palette.info;
  return palette.textSecondary;
}

function RequestRow({
  item,
  onPress,
  palette,
}: {
  item: AttendanceCorrectionRequestListItem;
  onPress: () => void;
  palette: ColorPalette;
}) {
  const dateLabel = formatCalendarDate(item.attendanceDate);
  const timeRange = `${formatPermissionTime(item.checkInTime)} - ${formatPermissionTime(item.checkOutTime)}`;
  const reason = item.reasonSummary || 'Attendance correction';

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${dateLabel}, ${item.statusLabel}`}
      onPress={onPress}
      style={[styles.row, { borderBottomColor: palette.border }]}
    >
      <View style={styles.rowLeft}>
        <Text style={[styles.rowTitle, { color: palette.textPrimary }]}>
          {dateLabel}
        </Text>
        <Text style={[styles.rowMeta, { color: palette.textSecondary }]}>
          {timeRange}
        </Text>
      </View>
      <View style={styles.rowRight}>
        <Text
          style={[
            styles.statusText,
            { color: statusTextColor(item.statusCode, palette) },
          ]}
        >
          {item.statusLabel}
        </Text>
        <Text style={[styles.rowMeta, { color: palette.textSecondary }]}>
          {reason}
        </Text>
      </View>
    </Pressable>
  );
}

function emptyDraft(): AttendanceCorrectionDraft {
  return draftFromAttendanceDetails(null);
}

function correctionTimesHelper(draft: AttendanceCorrectionDraft): string {
  const hasExisting =
    Boolean(draft.checkInTime.trim()) ||
    Boolean(draft.checkOutTime.trim()) ||
    draft.breaks.some(item => item.startTime.trim() || item.endTime.trim());
  const loaded = hasExisting
    ? 'Existing attendance for this date is loaded and can be edited.'
    : 'No attendance is saved for this date yet.';
  return `${loaded} Check-in is required. Check-out and breaks are optional.`;
}

function ApplyCorrectionForm({
  canApply,
  palette,
  initialAttendanceDate,
  onSuccess,
}: {
  canApply: boolean;
  palette: ColorPalette;
  initialAttendanceDate?: string;
  onSuccess: () => void;
}) {
  const today = useMemo(() => todayBusinessCalendarDate(), []);
  const maxDate = useMemo(
    () => parseCalendarDateInput(today) ?? undefined,
    [today],
  );
  const [attendanceDate, setAttendanceDate] = useState(
    initialAttendanceDate &&
      isAllowedAttendanceCorrectionDate(initialAttendanceDate)
      ? initialAttendanceDate
      : '',
  );
  const [draft, setDraft] = useState<AttendanceCorrectionDraft>(emptyDraft);
  const [dayLoading, setDayLoading] = useState(false);
  const [dayError, setDayError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const loadDay = useCallback(async (date: string) => {
    if (!isValidLeaveDate(date)) {
      setDraft(emptyDraft());
      setDayError(null);
      setDayLoading(false);
      return;
    }
    setDayLoading(true);
    try {
      const details = await getAttendanceDetails(date);
      setDraft(draftFromAttendanceDetails(details));
      setDayError(null);
    } catch (error) {
      const code = normalizeApiError(error).code;
      if (code === 'ATTENDANCE_NOT_FOUND' || code === 'NOT_FOUND') {
        setDraft(emptyDraft());
        setDayError(null);
      } else {
        setDraft(emptyDraft());
        setDayError(
          attendanceCorrectionErrorMessage(
            error,
            'Unable to load attendance for the selected date.',
          ),
        );
      }
    } finally {
      setDayLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!attendanceDate) {
      setDraft(emptyDraft());
      setDayError(null);
      return;
    }
    loadDay(attendanceDate).catch(() => undefined);
  }, [attendanceDate, loadDay]);

  const submit = async () => {
    if (!canApply || submitting || dayLoading) return;
    const trimmedDate = attendanceDate.trim();
    if (!isValidLeaveDate(trimmedDate)) {
      setSubmitError('Attendance date must use YYYY-MM-DD format.');
      return;
    }
    if (!isAllowedAttendanceCorrectionDate(trimmedDate)) {
      setSubmitError('Select today or an earlier attendance date.');
      return;
    }
    if (dayError) {
      setSubmitError(dayError);
      return;
    }
    if (draft.blocked) {
      setSubmitError(draft.blockReason);
      return;
    }
    const timeError = validateAttendanceCorrectionTimes({
      checkInTime: draft.checkInTime,
      checkOutTime: draft.checkOutTime,
      breaks: correctionBreaksPayload(draft.breaks),
      checkInLocked: draft.checkInLocked,
    });
    if (timeError) {
      setSubmitError(timeError);
      return;
    }
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setSubmitError('Reason is required.');
      return;
    }

    const breakTimes = joinedCorrectionBreakTimes(draft.breaks);
    setSubmitting(true);
    setSubmitError(null);
    try {
      await applyAttendanceCorrectionRequest({
        attendanceDate: trimmedDate,
        checkInTime: draft.checkInTime,
        checkOutTime: draft.checkOutTime,
        breakStartTime: breakTimes.breakStartTime,
        breakEndTime: breakTimes.breakEndTime,
        breaks: correctionBreaksPayload(draft.breaks),
        removedBreaks: correctionRemovedBreaksPayload(draft),
        reason: trimmedReason,
      });
      setAttendanceDate('');
      setDraft(emptyDraft());
      setReason('');
      onSuccess();
    } catch (error) {
      setSubmitError(
        attendanceCorrectionErrorMessage(
          error,
          'Unable to submit attendance correction request.',
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <KeyboardSafeForm>
      <RequestFormDateField
        label="Attendance date"
        required
        value={attendanceDate}
        maximumDate={maxDate}
        onChange={value => {
          setAttendanceDate(value);
          setSubmitError(null);
        }}
        editable={canApply}
      />
      {dayLoading ? (
        <Text style={[styles.helper, { color: palette.textSecondary }]}>
          Loading existing attendance…
        </Text>
      ) : null}
      {attendanceDate && !dayLoading && !draft.blocked ? (
        <Text style={[styles.helper, { color: palette.textSecondary }]}>
          {correctionTimesHelper(draft)}
        </Text>
      ) : null}
      <RequestFormTimeField
        label="Check-in"
        required
        value={draft.checkInTime}
        onChange={value => {
          setDraft(current =>
            current.checkInLocked
              ? current
              : { ...current, checkInTime: value },
          );
          setSubmitError(null);
        }}
        placeholder="Select time"
        editable={canApply && !draft.checkInLocked}
      />
      {draft.breaks.map((item, index) => {
        const labelPrefix =
          draft.breaks.length > 1 ? `Break ${index + 1}` : 'Break';
        const canRemove =
          canApply &&
          !draft.blocked &&
          draft.breaks.length > 1 &&
          !item.startLocked &&
          !item.endLocked;
        return (
          <View key={item.key} style={styles.breakBlock}>
            <RequestFormTimeField
              label={`${labelPrefix} start`}
              optional
              value={item.startTime}
              onChange={value => {
                setDraft(current =>
                  updateCorrectionBreak(current, item.key, { startTime: value }),
                );
                setSubmitError(null);
              }}
              placeholder="Select time"
              editable={canApply && !item.startLocked}
            />
            <RequestFormTimeField
              label={`${labelPrefix} end`}
              optional
              value={item.endTime}
              onChange={value => {
                setDraft(current =>
                  updateCorrectionBreak(current, item.key, { endTime: value }),
                );
                setSubmitError(null);
              }}
              placeholder="Select time"
              editable={canApply && !item.endLocked}
            />
            {canRemove ? (
              <AppButton
                title="Remove break"
                variant="text"
                onPress={() => {
                  setDraft(current => removeCorrectionBreak(current, item.key));
                  setSubmitError(null);
                }}
              />
            ) : null}
          </View>
        );
      })}
      {canApply && !draft.blocked ? (
        <AppButton
          title="Add another break"
          variant="secondary"
          onPress={() => {
            setDraft(current => addCorrectionBreak(current));
            setSubmitError(null);
          }}
        />
      ) : null}
      <RequestFormTimeField
        label="Check-out"
        optional
        value={draft.checkOutTime}
        onChange={value => {
          setDraft(current =>
            current.checkOutLocked
              ? current
              : { ...current, checkOutTime: value },
          );
          setSubmitError(null);
        }}
        placeholder="Select time"
        editable={canApply && !draft.checkOutLocked}
      />
      <RequestFormTextField
        label="Reason"
        required
        value={reason}
        onChangeText={value => {
          setReason(value);
          setSubmitError(null);
        }}
        placeholder="Enter reason"
        multiline
        editable={canApply && !draft.blocked}
      />
      {dayError ? (
        <Text accessibilityRole="alert" style={[styles.warning, { color: palette.error }]}>
          {dayError}
        </Text>
      ) : null}
      {draft.blockReason ? (
        <Text accessibilityRole="alert" style={[styles.warning, { color: palette.warning }]}>
          {draft.blockReason}
        </Text>
      ) : null}
      {submitError ? (
        <Text accessibilityRole="alert" style={[styles.warning, { color: palette.error }]}>
          {submitError}
        </Text>
      ) : null}
      {canApply ? (
        <AppButton
          title="Submit Request"
          onPress={submit}
          loading={submitting}
          disabled={submitting || dayLoading || draft.blocked || Boolean(dayError)}
        />
      ) : (
        <AppButton title="Submit Request" disabled onPress={() => undefined} />
      )}
    </KeyboardSafeForm>
  );
}

export function AttendanceCorrectionHomeScreen({ navigation, route }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const capabilities = getAttendanceCorrectionModuleCapabilities(
    auth.permissions,
  );
  const showTeamApprovals = canSeeRequestApprovals(
    auth.roles,
    auth.permissions,
    PERMISSIONS.ATTENDANCE_APPROVE,
  );
  const initialAttendanceDate = route.params?.initialAttendanceDate;

  const [activeTab, setActiveTab] = useState<CorrectionTab>(
    initialAttendanceDate ? 'Apply Correction' : 'My Requests',
  );
  const [items, setItems] = useState<AttendanceCorrectionRequestListItem[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const openDetails = useCallback(
    (item: AttendanceCorrectionRequestListItem) => {
      navigation.navigate(routes.attendanceCorrectionDetails, {
        correctionRequestId: item.correctionRequestId,
      });
    },
    [navigation],
  );

  const load = useCallback(
    async (isRefresh = false) => {
      if (!capabilities.canViewHistory) {
        setItems([]);
        setHistoryError(null);
        setLoading(false);
        setRefreshing(false);
        return;
      }

      isRefresh ? setRefreshing(true) : setLoading(true);
      try {
        const result = await getAttendanceCorrectionRequests({
          page: 1,
          pageSize: 20,
        });
        setItems(result.items);
        setHistoryError(null);
      } catch (error) {
        setItems([]);
        setHistoryError(
          attendanceCorrectionErrorMessage(
            error,
            'Unable to load attendance correction requests.',
          ),
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [capabilities.canViewHistory],
  );

  useFocusEffect(
    useCallback(() => {
      load().catch(() => undefined);
    }, [load]),
  );

  const header = useMemo(
    () => (
      <ScreenHeader
        bleed={false}
        title={
          activeTab === 'Apply Correction'
            ? 'Apply Correction'
            : 'Attendance Corrections'
        }
        onBack={navigation.goBack}
      />
    ),
    [activeTab, navigation.goBack],
  );

  return (
    <PermissionGuard
      anyPermissions={[
        PERMISSIONS.ATTENDANCE_VIEW,
        PERMISSIONS.ATTENDANCE_CORRECT,
        PERMISSIONS.ATTENDANCE_APPROVE,
      ]}
      fallback={<UnauthorizedScreen />}
    >
      {loading && items.length === 0 && !historyError ? (
        <AppScreen contentStyle={styles.screen} keyboardSafe>
          {header}
          <View style={styles.center}>
            <AppLoader message="Loading attendance corrections" />
          </View>
        </AppScreen>
      ) : (
        <AppScreen contentStyle={styles.screen} keyboardSafe>
          {header}
          {capabilities.canApply ? (
            <UnderlineTabs
              options={CORRECTION_TABS}
              value={activeTab}
              onChange={setActiveTab}
            />
          ) : null}
          {showTeamApprovals ? (
            <View style={styles.approvalAction}>
              <AppButton
                title={attendanceCorrectionApprovalListTitle(auth.roles)}
                onPress={() =>
                  navigation.navigate(routes.adminAttendanceCorrectionRequests)
                }
              />
            </View>
          ) : null}

          {activeTab === 'My Requests' || !capabilities.canApply ? (
            <View style={styles.body}>
              {historyError && items.length === 0 ? (
                <ErrorState message={historyError} onRetry={() => load()} />
              ) : (
                <FlatList
                  data={items}
                  keyExtractor={item => String(item.correctionRequestId)}
                  refreshControl={
                    <RefreshControl
                      refreshing={refreshing}
                      onRefresh={() => load(true)}
                      tintColor={palette.primary}
                    />
                  }
                  ListEmptyComponent={
                    <EmptyState
                      title="No correction requests"
                      message="No attendance correction requests were found."
                      actionTitle="Retry"
                      onAction={() => load()}
                    />
                  }
                  contentContainerStyle={styles.listContent}
                  renderItem={({ item }) => (
                    <RequestRow
                      item={item}
                      onPress={() => openDetails(item)}
                      palette={palette}
                    />
                  )}
                />
              )}
              {capabilities.canApply ? (
                <View style={styles.footer}>
                  <AppButton
                    title="Apply Correction"
                    onPress={() => setActiveTab('Apply Correction')}
                  />
                </View>
              ) : null}
            </View>
          ) : (
            <ApplyCorrectionForm
              canApply={capabilities.canApply}
              palette={palette}
              initialAttendanceDate={initialAttendanceDate}
              onSuccess={() => {
                setActiveTab('My Requests');
                load().catch(() => undefined);
              }}
            />
          )}
        </AppScreen>
      )}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  center: { flex: 1, justifyContent: 'center' },
  body: { flex: 1 },
  approvalAction: {
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.sm,
  },
  listContent: { flexGrow: 1, paddingBottom: spacing.md },
  row: {
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.lg,
    borderBottomWidth: StyleSheet.hairlineWidth,
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  rowLeft: { flex: 1, gap: spacing.xs },
  rowRight: { alignItems: 'flex-end', gap: spacing.xs, maxWidth: '48%' },
  rowTitle: { ...typography.body, fontWeight: '700' },
  rowMeta: { ...typography.caption },
  statusText: { ...typography.body, fontWeight: '700' },
  footer: {
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.md,
    paddingBottom: spacing.xl,
  },
  applyScreen: {
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.lg,
    paddingBottom: spacing.xl,
  },
  form: { gap: spacing.lg },
  breakBlock: { gap: spacing.lg },
  warning: { ...typography.caption },
  helper: { ...typography.caption },
});
